/**
 * The widget's brand-colour maths, and the save-time check built on it.
 *
 * ONE SOURCE, THREE READERS. The widget calls `renderedBrandColors` to decide
 * what it actually paints (branding.ts `brandingVars`); the partner portal
 * imports this file for its contrast panel, notes and one-click fixes; the
 * server keeps a byte-identical twin (`server/src/services/widget-branding-check.ts`)
 * because its build cannot reach package sources, and
 * `server/tests/unit/widget-branding-check.test.ts` fails the moment the two
 * bodies differ. So the colour a note shows, the colour the preview paints
 * and the colour a visitor sees cannot drift apart.
 *
 * No imports, on purpose: that is what lets the server test read this file
 * and the portal bundle it without pulling in Preact or the stylesheet.
 */

// ---- BEGIN SHARED BODY ---- keep byte-identical with the twin named above.

export type Rgb = [number, number, number];

/** `#rgb` / `#rrggbb` -> [r,g,b] 0-255, or null if it is neither. */
export function parseHex(color: string): Rgb | null {
  const value = color.trim();
  if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) return null;
  const hex =
    value.length === 4
      ? value.charAt(1).repeat(2) + value.charAt(2).repeat(2) + value.charAt(3).repeat(2)
      : value.slice(1);
  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
}

/** WCAG 2.x relative luminance (sRGB). */
export function relativeLuminance(rgb: Rgb): number {
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours; 1 when either is unreadable. */
export function contrastRatio(a: string, b: string): number {
  const rgbA = parseHex(a);
  const rgbB = parseHex(b);
  if (!rgbA || !rgbB) return 1;
  const lumA = relativeLuminance(rgbA);
  const lumB = relativeLuminance(rgbB);
  const light = Math.max(lumA, lumB);
  const dark = Math.min(lumA, lumB);
  return (light + 0.05) / (dark + 0.05);
}

/** Text contrast floor for normal-size text (WCAG SC 1.4.3 AA). */
export const TEXT_CONTRAST_MIN = 4.5;

/** Non-text contrast floor for a control's boundary or focus mark (WCAG SC 1.4.11). */
export const EDGE_CONTRAST_MIN = 3;

/** Black or white, whichever reads better on `background`. Never under 4.58:1. */
export function blackOrWhite(background: string): string {
  return contrastRatio(background, '#ffffff') >= contrastRatio(background, '#000000') ? '#ffffff' : '#000000';
}

/**
 * The colour to put ON `primary`.
 *
 * - No `primary`: nothing to compute against, so no override at all.
 * - `on_primary` served and it clears 4.5:1: the partner's choice is honoured
 *   verbatim — this never "improves" a pair that already passes.
 * - Otherwise (null, or a served pair that fails): black or white, whichever
 *   has the higher ratio against `primary`. Both are always computable, and
 *   one of them always beats a failing hand-picked value, so this cannot
 *   return something worse than what it replaced.
 */
export function resolveOnPrimary(primary: string | null, onPrimary: string | null): string | null {
  if (!primary || !parseHex(primary)) return null;
  if (onPrimary && parseHex(onPrimary) && contrastRatio(primary, onPrimary) >= TEXT_CONTRAST_MIN) {
    return onPrimary;
  }
  return blackOrWhite(primary);
}

/** Null when `color` clears `min` on `background`; else `fallback` when that
 *  does; else black or white. The shape every brand fallback below shares. */
function readableOn(color: string, background: string, fallback: string, min: number): string | null {
  if (contrastRatio(color, background) >= min) return null;
  return contrastRatio(fallback, background) >= min ? fallback : blackOrWhite(background);
}

/** The lowest ratio `color` has against any of `backgrounds`. */
function worstRatio(color: string, backgrounds: string[]): number {
  return Math.min(...backgrounds.map((background) => contrastRatio(color, background)));
}

/** `readableOn` for text that sits on the card AND on the raised containers
 *  inside it (the clarify block, the teaser card): it has to read on both. */
function readableOnAll(color: string, backgrounds: string[], fallback: string, min: number): string | null {
  if (worstRatio(color, backgrounds) >= min) return null;
  if (worstRatio(fallback, backgrounds) >= min) return fallback;
  return worstRatio('#ffffff', backgrounds) >= worstRatio('#000000', backgrounds) ? '#ffffff' : '#000000';
}

/**
 * The edge to draw inside a filled primary button whose colour blends into
 * the card (#902: a navy `#14213d` button on the dark card, 1.3:1), or null
 * when the fill already stands out. Also the focus/outline mark (see
 * `renderedBrandColors`). Falls back to the card's text colour family, then
 * to black or white when that is not 3:1 against the card either.
 */
export function primaryEdge(primary: string, surface: string, text: string): string | null {
  return readableOn(primary, surface, text, EDGE_CONTRAST_MIN);
}

/**
 * The colour to use where the partner's `primary` is TEXT on the card (the
 * live "Finish" button, the "You" speaker label), or null when `primary` is
 * already readable there. A light brand colour such as a safety yellow
 * `#f6c90e` is 1.6:1 on the light card: fine as a button fill, unreadable as
 * text (2026-09-26 summit.25x.codes install). Falls back to the card's text
 * colour, then to black or white, whichever reads.
 */
export function primaryAsText(primary: string, surface: string, text: string, raised: string = surface): string | null {
  return readableOnAll(primary, [surface, raised], text, TEXT_CONTRAST_MIN);
}

/** `color-mix(in srgb, a <weight>, b)` for two hex colours, as `#rrggbb`;
 *  null when either is unreadable. */
export function mixHex(a: string, b: string, weight: number): string | null {
  const rgbA = parseHex(a);
  const rgbB = parseHex(b);
  if (!rgbA || !rgbB) return null;
  return `#${rgbA
    .map((channel, i) => Math.round(channel * weight + (rgbB[i] ?? 0) * (1 - weight)).toString(16).padStart(2, '0'))
    .join('')}`;
}

/** How much brand goes into the capture tile's tint, per theme: about where
 *  the widget's own `secondaryContainer` sits in each (#921). */
export const TINT_WEIGHT = { light: 0.2, dark: 0.4 } as const;

/** How far the tint must stand off the card to read as a tile at all
 *  (today's lavender is 1.3:1 on the light card). */
const TINT_CONTRAST_MIN = 1.25;

/**
 * The capture tile's brand tint (#921) and the glyph drawn on it. The tint is
 * `primary` mixed into the card, with more brand in steps while it would
 * vanish (a pale yellow on the light card); a brand the card swallows even
 * at full strength tints with the contrast-safe mark instead. The glyph is
 * `primary` when it clears 3:1 on the tint (null: the stylesheet falls back
 * to it), else the text colour, else black or white.
 */
export function captureTint(
  primary: string,
  surface: string,
  text: string,
  weight: number,
): { tint: string; glyph: string | null } | null {
  let tint: string | null = null;
  for (let w = weight; ; w = Math.min(1, w + weight)) {
    tint = mixHex(primary, surface, w);
    if (!tint) return null;
    if (contrastRatio(tint, surface) >= TINT_CONTRAST_MIN || w === 1) break;
  }
  if (contrastRatio(tint, surface) < TINT_CONTRAST_MIN) {
    tint = mixHex(primaryEdge(primary, surface, text) ?? primary, surface, weight) ?? tint;
  }
  return { tint, glyph: readableOn(primary, tint, text, EDGE_CONTRAST_MIN) };
}

/**
 * The secondary text colour (consent, SMS/eLocal boxes, the notice, labels,
 * the subheading, the result's body copy, status lines) on a partner card:
 * the card's text mixed toward the card as far as 4.5:1 allows on the card
 * AND on `raised` (the teaser card and clarify block sit inside it), falling
 * back to the text itself and then black or white.
 */
export function mutedText(surface: string, text: string, raised: string = surface): string {
  for (const weight of [0.72, 0.8, 0.88]) {
    const muted = mixHex(text, surface, weight);
    if (muted && worstRatio(muted, [surface, raised]) >= TEXT_CONTRAST_MIN) return muted;
  }
  return readableOnAll(text, [surface, raised], text, TEXT_CONTRAST_MIN) ?? text;
}

/** How much of the card's text goes into a raised container's fill (the mode
 *  toggle, clarify block, teaser card, talk button, live transcript) on a
 *  partner card: a quiet step off the card, as the theme's own
 *  surface-container-low is. */
export const RAISED_WEIGHT = 0.06;

/**
 * The raised-container fill on a partner card. Without it those containers
 * kept the THEME's fill: a near-black box inside a white partner card in dark
 * mode, with brand text colours computed for the card painted on it. The step
 * shrinks when it would take the partner's own text pair under what it has on
 * the card itself, so a raised container never reads worse than the card.
 */
export function raisedSurface(surface: string, text: string): string {
  const floor = Math.min(TEXT_CONTRAST_MIN, contrastRatio(text, surface));
  for (const weight of [RAISED_WEIGHT, RAISED_WEIGHT * 2 / 3, RAISED_WEIGHT / 3]) {
    const raised = mixHex(text, surface, weight);
    if (raised && contrastRatio(text, raised) >= floor) return raised;
  }
  return surface;
}

export type BrandTheme = 'light' | 'dark';

export interface BrandColors {
  primary: string | null;
  on_primary: string | null;
  surface: string | null;
  text: string | null;
  /** The card outline and its inner hairlines (optional: older callers send four colours). */
  border?: string | null;
}

/** How much of the card's text goes into the derived outline on a partner
 *  card: about where the widget's own outline-variant sits on its own card
 *  (1.4:1 in light mode), stepped off the partner's card instead of lavender. */
export const BORDER_WEIGHT = 0.16;

/**
 * The four theme colours the brand maths reads, per theme. They are the
 * widget's LIGHT_TOKENS / DARK_TOKENS values (styles.ts); the widget's
 * brandContrast.test.ts fails if the two disagree.
 */
export const CARD_TOKENS: Record<
  BrandTheme,
  { primary: string; surface: string; surfaceContainerLow: string; onSurface: string; onSurfaceVariant: string; error: string }
> = {
  light: { primary: '#c300b0', surface: '#fcf9ff', surfaceContainerLow: '#f2eeff', onSurface: '#2e2b3d', onSurfaceVariant: '#555267', error: '#da003a' },
  dark: { primary: '#ff82f5', surface: '#0c0818', surfaceContainerLow: '#161324', onSurface: '#ebe8ff', onSurfaceVariant: '#bebad2', error: '#ff979d' },
};

/** What the widget paints for one theme. Null = no override, the stylesheet's
 *  own fallback (the partner colour, or the bundle's) applies. */
export interface RenderedBrand {
  theme: BrandTheme;
  surface: string;
  /** The text colour on the card: `derivedText`, else the partner's, else the theme's. */
  text: string;
  /** Set when the partner's card would otherwise carry unreadable text
   *  (under 4.5:1): a partner surface with no text colour, where the theme's
   *  own text fails on it (the dark theme's light text on a white card) ->
   *  the other theme's text, else black or white; or a partner text colour
   *  with no surface, where that text fails on this theme's own card (dark
   *  text in dark mode) -> the theme's own text. */
  derivedText: string | null;
  /** Focus ring, focused-field border, talk/Finish borders, capture icon,
   *  speaking bars, level meter (3:1). The capture tile's hover border and
   *  the consent checkbox keep the brand on purpose (#921): the focus ring
   *  and the checkmark carry their state. */
  mark: string | null;
  /** 2px inner edge on a filled primary button (3:1). */
  edge: string | null;
  /** Where the brand colour is text (4.5:1). */
  accentText: string | null;
  tint: string | null;
  glyph: string | null;
  muted: string | null;
  /** The raised containers' fill (`raisedSurface`), set whenever the partner
   *  set a surface or text colour; null keeps the theme's container. */
  raised: string | null;
  /** Error text on the card (field errors, the required mark, a failed
   *  upload): the theme's error red unless it is under 4.5:1 on a partner
   *  card (dark mode's pink on a white card), then the other theme's, then
   *  the nearest shade of it that reads. */
  error: string | null;
  /** The card outline and the hairlines inside it (mode tabs, clarify and
   *  teaser boxes): the partner's `border`, else on a partner card the text
   *  mixed into the card (`BORDER_WEIGHT`); null keeps the theme's own.
   *  Decorative, so no contrast floor: form fields and secondary buttons
   *  keep the theme's `outline`, which is their 3:1 boundary. */
  border: string | null;
}

/** Exactly what the widget renders for `colors` in `theme` (branding.ts
 *  writes each non-null value as a `--fx-<name>-<theme>` property). */
export function renderedBrandColors(colors: BrandColors, theme: BrandTheme): RenderedBrand {
  const tokens = CARD_TOKENS[theme];
  const surface = colors.surface ?? tokens.surface;
  const other = CARD_TOKENS[theme === 'light' ? 'dark' : 'light'];
  const derivedText =
    !colors.text && colors.surface
      ? readableOn(tokens.onSurface, surface, other.onSurface, TEXT_CONTRAST_MIN)
      : colors.text && !colors.surface && contrastRatio(colors.text, surface) + 1e-9 < TEXT_CONTRAST_MIN
        ? tokens.onSurface
        : null;
  const text = derivedText ?? colors.text ?? tokens.onSurface;
  const out: RenderedBrand = {
    theme,
    surface,
    text,
    derivedText,
    mark: null,
    edge: null,
    accentText: null,
    tint: null,
    glyph: null,
    muted: null,
    raised: null,
    error: null,
    border: null,
  };
  const partnerCard = Boolean(colors.surface || colors.text);
  const raised = partnerCard ? raisedSurface(surface, text) : tokens.surfaceContainerLow;
  if (colors.primary) {
    out.edge = primaryEdge(colors.primary, surface, derivedText ?? colors.text ?? tokens.onSurfaceVariant);
    const tile = captureTint(colors.primary, surface, text, TINT_WEIGHT[theme]);
    if (tile) {
      out.tint = tile.tint;
      out.glyph = tile.glyph;
    }
  }
  // Where the brand is TEXT (4.5:1), on the card and in its raised
  // containers. A partner card under the default magenta gets the same check.
  if (colors.primary || partnerCard) out.accentText = primaryAsText(colors.primary ?? tokens.primary, surface, text, raised);
  // Where the brand is a non-text MARK (3:1, WCAG 1.4.11). A partner surface
  // under the default magenta gets the same check.
  if (colors.primary || colors.surface) out.mark = primaryEdge(colors.primary ?? tokens.primary, surface, text);
  out.border = colors.border ?? (partnerCard ? mixHex(text, surface, BORDER_WEIGHT) : null);
  if (partnerCard) {
    out.muted = mutedText(surface, text, raised);
    out.raised = raised;
    const reads = (c: string) => worstRatio(c, [surface, raised]) >= TEXT_CONTRAST_MIN;
    if (!reads(tokens.error)) {
      out.error = reads(other.error) ? other.error : (nearestShade(other.error, reads) ?? blackOrWhite(surface));
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Save-time check: blocking vs advisory, with one-click fixes
// ---------------------------------------------------------------------------

/** HSL with h 0-360, s and l 0-1. */
function rgbToHsl(rgb: Rgb): [number, number, number] {
  const [r, g, b] = rgb.map((c) => c / 255) as Rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  return [h < 0 ? h + 360 : h, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return `#${[r, g, b].map((v) => Math.round(Math.min(1, Math.max(0, v + m)) * 255).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The nearest shade of `color`'s hue (same HSL hue and saturation, lightness
 * moved in 0.5% steps, whichever direction gets there first) that `accept`
 * takes; null when none does.
 */
export function nearestShade(color: string, accept: (hex: string) => boolean): string | null {
  const rgb = parseHex(color);
  if (!rgb) return null;
  const [h, s, l] = rgbToHsl(rgb);
  for (let step = 1; step <= 200; step++) {
    for (const candidate of [l - step / 200, l + step / 200]) {
      if (candidate < 0 || candidate > 1) continue;
      const hex = hslToHex(h, s, candidate);
      if (accept(hex)) return hex;
    }
  }
  return null;
}

/** A plain colour word for messages: "yellow", "navy", "white", "grey". */
export function colourName(color: string): string {
  const rgb = parseHex(color);
  if (!rgb) return 'colour';
  const [h, s, l] = rgbToHsl(rgb);
  if (l >= 0.96) return 'white';
  if (l <= 0.06) return 'black';
  if (s < 0.15) return 'grey';
  if (h < 15 || h >= 345) return l > 0.75 ? 'pink' : 'red';
  if (h < 40) return l < 0.35 ? 'brown' : 'orange';
  if (h < 68) return 'yellow';
  if (h < 160) return 'green';
  if (h < 195) return 'teal';
  if (h < 250) return l < 0.3 ? 'navy' : 'blue';
  if (h < 290) return 'purple';
  return 'pink';
}

/** Ratios as the portal shows them: two decimals, trailing zeros dropped. */
export function formatRatio(ratio: number): string {
  return `${Math.round(ratio * 100) / 100}`;
}

export type ContrastPair = 'text_on_surface' | 'on_primary_on_primary' | 'primary_on_surface' | 'border_on_surface';
export type BrandColorField = 'primary' | 'on_primary' | 'text' | 'surface' | 'border';

/** A message as text runs and colour chips; `message` is the same joined. */
export type MessagePart = string | { color: string };

export interface BrandingSuggestion {
  pair: ContrastPair;
  /** e.g. "Use a darker primary: #b28f0a" — also the button text. */
  label: string;
  /** Field -> value to fill in. */
  changes: Partial<Record<BrandColorField, string>>;
  /** Every checked pair's ratio once the change is applied. */
  ratios: Partial<Record<ContrastPair, number>>;
}

export interface BrandingCheck {
  pair: ContrastPair;
  label: string;
  /** The field the result belongs to, e.g. `branding.colors.primary`. */
  field: string;
  foreground: string | null;
  background: string | null;
  /** Rounded to 2 dp; null when a half is unset (the bundle's own colour). */
  ratio: number | null;
  required: number;
  /**
   * ok: passes (or unchecked because a half is unset).
   * advisory: fails as entered, but the widget draws `rendered` instead,
   *   which passes; save and publish go ahead.
   * blocking: text the widget cannot repair; save is refused.
   */
  status: 'ok' | 'advisory' | 'blocking';
  /** `on_primary` left on Auto: the ratio is the widget's black-or-white. */
  auto: boolean;
  /** Advisory only: what the widget draws instead, per theme in play. */
  rendered: Array<{ theme: BrandTheme; color: string; ratio: number }>;
  message: string | null;
  parts: MessagePart[];
  suggestions: BrandingSuggestion[];
}

export interface BrandingCheckResult {
  checks: BrandingCheck[];
  blocking: BrandingCheck[];
  advisory: BrandingCheck[];
  suggestions: BrandingSuggestion[];
}

const PAIR_LABELS: Record<ContrastPair, string> = {
  text_on_surface: 'Text on surface',
  on_primary_on_primary: 'Button text on primary',
  primary_on_surface: 'Primary on surface (focus ring)',
  border_on_surface: 'Border on surface (decorative, no minimum)',
};

/** The border is decorative (see `RenderedBrand.border`): reported, never failed. */
const BORDER_CONTRAST_MIN = 1;

function validHex(color: string | null): string | null {
  return color && parseHex(color) ? color.trim().toLowerCase() : null;
}

function join(parts: MessagePart[]): string {
  return parts.map((part) => (typeof part === 'string' ? part : part.color)).join('');
}

/** "#aaa (3.1:1)" in light mode and "#bbb (4:1)" in dark mode — or one chip when both agree. */
function renderedParts(rendered: BrandingCheck['rendered'], nameTheme = false): MessagePart[] {
  const distinct = rendered.filter((r, i) => rendered.findIndex((o) => o.color === r.color) === i);
  const parts: MessagePart[] = [];
  distinct.forEach((r, i) => {
    if (i > 0) parts.push(' and ');
    parts.push({ color: r.color }, ` (${formatRatio(r.ratio)}:1)`);
    if (distinct.length > 1 || nameTheme) parts.push(` in ${r.theme} mode`);
  });
  return parts;
}

function ratiosFor(colors: BrandColors): Partial<Record<ContrastPair, number>> {
  const out: Partial<Record<ContrastPair, number>> = {};
  if (colors.text && colors.surface) out.text_on_surface = Math.round(contrastRatio(colors.text, colors.surface) * 100) / 100;
  const onPrimary = resolveOnPrimary(colors.primary, colors.on_primary);
  if (colors.primary && onPrimary) out.on_primary_on_primary = Math.round(contrastRatio(onPrimary, colors.primary) * 100) / 100;
  if (colors.primary && colors.surface) out.primary_on_surface = Math.round(contrastRatio(colors.primary, colors.surface) * 100) / 100;
  return out;
}

/**
 * THE branding colour check — the portal's live panel, its save gate, the
 * server's `PUT /config/draft` and any API-driven setup all call this and
 * nothing else, so one input is accepted or refused the same way everywhere.
 *
 * Three pairs, each checked only when both halves are set (a null half is the
 * bundle's own accessible colour), and a fourth reported only when `border`
 * is set (border_on_surface: decorative, no minimum, never fails):
 *
 * - text_on_surface (4.5:1) — BLOCKING. Body text is drawn in the partner's
 *   colour verbatim; the widget has no substitute for it. Suggests the
 *   nearest shade of the same hue that passes.
 * - on_primary_on_primary (4.5:1) — ADVISORY. `resolveOnPrimary` swaps a
 *   failing button-text colour for black or white (always >= 4.58:1).
 *   Suggests filling that value in.
 * - primary_on_surface (3:1) — ADVISORY. Every place the brand is a mark on
 *   the card (focus ring, outlines, borders, meters) uses `mark`, which falls
 *   back to the text colour or black/white; button fills keep the brand and
 *   gain an `edge`. Suggests the nearest shade of the brand hue that passes
 *   3:1 on the card while its button text still clears 4.5:1.
 *
 * `theme` picks which theme's tokens back an unset `text` ('auto': both).
 */
export function checkBrandingColors(
  input: BrandColors,
  theme: 'auto' | BrandTheme = 'auto',
): BrandingCheckResult {
  const colors: BrandColors = {
    primary: validHex(input.primary),
    on_primary: validHex(input.on_primary),
    surface: validHex(input.surface),
    text: validHex(input.text),
    border: validHex(input.border ?? null),
  };
  const themes: BrandTheme[] = theme === 'auto' ? ['light', 'dark'] : [theme];
  const checks: BrandingCheck[] = [];
  const base = (pair: ContrastPair, field: BrandColorField, fg: string | null, bg: string | null, required: number): BrandingCheck => ({
    pair,
    label: PAIR_LABELS[pair],
    field: `branding.colors.${field}`,
    foreground: fg,
    background: bg,
    ratio: fg && bg ? Math.round(contrastRatio(fg, bg) * 100) / 100 : null,
    required,
    status: 'ok',
    auto: false,
    rendered: [],
    message: null,
    parts: [],
    suggestions: [],
  });
  const fails = (fg: string, bg: string, required: number) => contrastRatio(fg, bg) + 1e-9 < required;

  // 1. Body text on the card: blocking.
  const text = base('text_on_surface', 'text', colors.text, colors.surface, TEXT_CONTRAST_MIN);
  if (colors.text && colors.surface && fails(colors.text, colors.surface, TEXT_CONTRAST_MIN)) {
    const surface = colors.surface;
    text.status = 'blocking';
    const shade = nearestShade(colors.text, (hex) => !fails(hex, surface, TEXT_CONTRAST_MIN));
    const fix = shade ?? blackOrWhite(surface);
    const fixRatio = contrastRatio(fix, surface);
    text.parts = [
      `Text on surface is ${formatRatio(contrastRatio(colors.text, surface))}:1; it needs at least ${TEXT_CONTRAST_MIN}:1. Try `,
      { color: fix },
      ` (${formatRatio(fixRatio)}:1).`,
    ];
    text.suggestions.push({
      pair: 'text_on_surface',
      label: `Use ${fix} for text (${formatRatio(fixRatio)}:1)`,
      changes: { text: fix },
      ratios: ratiosFor({ ...colors, text: fix }),
    });
  }
  // 1b. A card colour with no text colour: advisory. Where the theme's own
  // text would fail on the partner's card (the dark theme's light text on a
  // white card), the widget writes the derived text instead.
  if (!colors.text && colors.surface) {
    const surface = colors.surface;
    const renders = themes.map((t) => renderedBrandColors(colors, t)).filter((r) => r.derivedText);
    if (renders.length) {
      const worst = renders
        .map((r) => ({ theme: r.theme, color: CARD_TOKENS[r.theme].onSurface }))
        .sort((a, b) => contrastRatio(a.color, surface) - contrastRatio(b.color, surface))[0] as { theme: BrandTheme; color: string };
      text.status = 'advisory';
      text.foreground = worst.color;
      text.ratio = Math.round(contrastRatio(worst.color, surface) * 100) / 100;
      text.rendered = renders.map((r) => ({ theme: r.theme, color: r.text, ratio: contrastRatio(r.text, surface) }));
      const card = colourName(surface) === 'white' || colourName(surface) === 'black' ? colourName(surface) : blackOrWhite(surface) === '#000000' ? 'light' : 'dark';
      text.parts = [
        `You left Text blank, and the widget's own ${worst.theme}-mode text (`,
        { color: worst.color },
        `) is ${formatRatio(contrastRatio(worst.color, surface))}:1 on your ${card} card, so we'll use `,
        ...renderedParts(text.rendered, renders.length < themes.length),
        ' for text on your card instead.',
      ];
      const fix = renders[0]?.text as string;
      text.suggestions.push({
        pair: 'text_on_surface',
        label: `Use ${fix} for text (${formatRatio(contrastRatio(fix, surface))}:1)`,
        changes: { text: fix },
        ratios: ratiosFor({ ...colors, text: fix }),
      });
    }
  }
  // 1c. A text colour with no card colour: advisory. The text sits on the
  // widget's own card, and no colour reads on both the light and the dark
  // card, so under `auto` it fails in one of them (dark text is ~1.05:1 on
  // the dark card). There the widget keeps the theme's own text instead.
  if (colors.text && !colors.surface) {
    const setText = colors.text;
    const renders = themes.map((t) => renderedBrandColors(colors, t)).filter((r) => r.derivedText);
    const worst = renders[0];
    if (worst) {
      const card = worst.surface;
      const fix = blackOrWhite(setText);
      const fixRatio = contrastRatio(setText, fix);
      text.status = 'advisory';
      text.background = card;
      text.ratio = Math.round(contrastRatio(setText, card) * 100) / 100;
      text.rendered = renders.map((r) => ({ theme: r.theme, color: r.text, ratio: contrastRatio(r.text, r.surface) }));
      text.parts = [
        `You left Card background blank, and your text is ${formatRatio(contrastRatio(setText, card))}:1 on the widget's own ${worst.theme}-mode card (`,
        { color: card },
        `), so we'll use `,
        ...renderedParts(text.rendered, renders.length < themes.length),
        ` for text instead. To use your text in both modes, set a card background such as `,
        { color: fix },
        ` (${formatRatio(fixRatio)}:1).`,
      ];
      text.suggestions.push({
        pair: 'text_on_surface',
        label: `Use ${fix} for the card background (${formatRatio(fixRatio)}:1)`,
        changes: { surface: fix },
        ratios: ratiosFor({ ...colors, surface: fix }),
      });
    }
  }
  checks.push(text);

  // 2. Button text on the button: advisory, the widget swaps in black/white.
  const resolvedOn = resolveOnPrimary(colors.primary, colors.on_primary);
  const onPrimary = base('on_primary_on_primary', 'on_primary', colors.on_primary ?? resolvedOn, colors.primary, TEXT_CONTRAST_MIN);
  onPrimary.auto = !colors.on_primary && Boolean(resolvedOn);
  if (colors.primary && colors.on_primary && resolvedOn && fails(colors.on_primary, colors.primary, TEXT_CONTRAST_MIN)) {
    const ratio = contrastRatio(resolvedOn, colors.primary);
    onPrimary.status = 'advisory';
    onPrimary.rendered = themes.map((t) => ({ theme: t, color: resolvedOn, ratio }));
    onPrimary.parts = [
      `Your button text is ${formatRatio(contrastRatio(colors.on_primary, colors.primary))}:1 on your ${colourName(colors.primary)} buttons (text needs ${TEXT_CONTRAST_MIN}:1), so the widget writes it in `,
      { color: resolvedOn },
      ` (${formatRatio(ratio)}:1) instead.`,
    ];
    onPrimary.suggestions.push({
      pair: 'on_primary_on_primary',
      label: `Use ${resolvedOn} for button text`,
      changes: { on_primary: resolvedOn },
      ratios: ratiosFor({ ...colors, on_primary: resolvedOn }),
    });
  }
  checks.push(onPrimary);

  // 3. The brand as a mark on the card: advisory, the widget draws `mark`.
  const mark = base('primary_on_surface', 'primary', colors.primary, colors.surface, EDGE_CONTRAST_MIN);
  if (colors.primary && colors.surface && fails(colors.primary, colors.surface, EDGE_CONTRAST_MIN)) {
    const primary = colors.primary;
    const surface = colors.surface;
    const renders = themes.map((t) => renderedBrandColors(colors, t));
    mark.status = 'advisory';
    mark.rendered = renders.map((r) => {
      const color = r.mark ?? primary;
      return { theme: r.theme, color, ratio: contrastRatio(color, r.surface) };
    });
    const name = colourName(primary);
    const lightCard = blackOrWhite(surface) === '#000000';
    const card = colourName(surface) === 'white' || colourName(surface) === 'black' ? colourName(surface) : lightCard ? 'light' : 'dark';
    const edges = renders.map((r) => r.edge).filter((e): e is string => Boolean(e));
    const edgeNote: MessagePart[] =
      edges.length && edges.every((e) => e === edges[0]) ? [' fill, with a thin ', { color: edges[0] as string }, ' edge'] : [' fill'];
    mark.parts = [
      `Your ${name} is ${lightCard ? 'light' : 'dark'} on a ${card} card (${formatRatio(contrastRatio(primary, surface))}:1; outlines need ${EDGE_CONTRAST_MIN}:1), so the widget draws the focus ring and outlines in `,
      ...renderedParts(mark.rendered),
      ` instead. Buttons keep your ${name}`,
      ...edgeNote,
      '.',
    ];
    // Keep the partner's own button text readable on the new shade — unless
    // it already fails on their colour, in which case the widget swaps it
    // for black or white either way.
    const setOn = colors.on_primary && !fails(colors.on_primary, primary, TEXT_CONTRAST_MIN) ? colors.on_primary : null;
    const passesSurface = (hex: string) => !fails(hex, surface, EDGE_CONTRAST_MIN);
    let shade = nearestShade(primary, (hex) => passesSurface(hex) && (!setOn || !fails(setOn, hex, TEXT_CONTRAST_MIN)));
    const changes: Partial<Record<BrandColorField, string>> = {};
    if (shade) changes.primary = shade;
    else if (setOn) {
      // No shade keeps the partner's button text readable too: change both.
      shade = nearestShade(primary, passesSurface);
      if (shade) {
        changes.primary = shade;
        changes.on_primary = blackOrWhite(shade);
      }
    }
    if (shade) {
      const darker = relativeLuminance(parseHex(shade) as Rgb) < relativeLuminance(parseHex(primary) as Rgb);
      const next = { ...colors, ...changes };
      mark.suggestions.push({
        pair: 'primary_on_surface',
        label:
          `Use a ${darker ? 'darker' : 'lighter'} primary: ${shade} (${formatRatio(contrastRatio(shade, surface))}:1)` +
          (changes.on_primary ? ` with ${changes.on_primary} button text` : ''),
        changes,
        ratios: ratiosFor(next),
      });
    }
  }
  checks.push(mark);

  // 4. The border: decorative (WCAG 1.4.11 does not apply to a card
  // outline), so it is reported with its ratio and never fails. Only when
  // the partner set one: an unset border is derived from their card.
  if (colors.border) checks.push(base('border_on_surface', 'border', colors.border, colors.surface, BORDER_CONTRAST_MIN));

  for (const check of checks) check.message = check.parts.length ? join(check.parts) : null;
  return {
    checks,
    blocking: checks.filter((c) => c.status === 'blocking'),
    advisory: checks.filter((c) => c.status === 'advisory'),
    suggestions: checks.flatMap((c) => c.suggestions),
  };
}

// ---- END SHARED BODY ----
